import { Box, Flex, Text, useColorModeValue, chakra } from '@chakra-ui/react';
import React from 'react';

import type { Route } from 'nextjs-routes';

import Skeleton from 'ui/shared/chakra/Skeleton';
import Hint from 'ui/shared/Hint';
import IconSvg, { type IconName } from 'ui/shared/IconSvg';
import NextLink from 'ui/shared/NextLink';
import TruncatedValue from 'ui/shared/TruncatedValue';

export type Props = {
  className?: string;
  label: string;
  value: string | React.ReactNode;
  valuePrefix?: string;
  valuePostfix?: string;
  hint?: string | React.ReactNode;
  isLoading?: boolean;
  diff?: string | number;
  diffFormatted?: string;
  diffPeriod?: '24h';
  period?: '1h' | '24h' | '30min';
  href?: Route;
  icon?: IconName;
};

const Container = ({ href, children }: { href?: Route; children: React.JSX.Element }) => {
  if (href) {
    return (
      <NextLink href={ href } passHref legacyBehavior>
        { children }
      </NextLink>
    );
  }

  return children;
};

const StatsWidget = ({
  className,
  icon,
  label,
  value,
  valuePrefix,
  valuePostfix,
  isLoading,
  hint,
  diff,
  diffPeriod = '24h',
  diffFormatted,
  period,
  href,
}: Props) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const skeletonBgColor = useColorModeValue('gray.100', 'gray.700');
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const valueColor = useColorModeValue('gray.900', 'white');

  return (
    <Container href={ !isLoading ? href : undefined }>
      <Flex
        className={ className }
        alignItems="center"
        bg={ isLoading ? skeletonBgColor : bgColor }
        p={ 4 }
        borderRadius="xl"
        border="1px solid"
        borderColor={ isLoading ? 'transparent' : 'blue.100' }
        justifyContent="flex-start"
        columnGap={ 3 }
        boxShadow="0 1px 4px rgba(37,99,235,0.06)"
        transition="all 0.2s ease"
        _hover={ !isLoading ? {
          borderColor: 'blue.200',
          boxShadow: '0 8px 24px rgba(37,99,235,0.12)',
          transform: 'translateY(-2px)',
        } : {} }
        { ...(href && !isLoading ? {
          as: 'a',
          href,
          cursor: 'pointer',
        } : {}) }
      >
        { icon && (
          <Box
            display={{ base: 'none', lg: 'flex' }}
            alignItems="center"
            justifyContent="center"
            w="46px"
            h="46px"
            borderRadius="lg"
            bg={ isLoading ? 'transparent' : 'blue.50' }
            border="1px solid"
            borderColor={ isLoading ? 'transparent' : 'blue.100' }
            flexShrink={ 0 }
          >
            <IconSvg
              name={ icon }
              boxSize="22px"
              isLoading={ isLoading }
              color={ isLoading ? 'gray.300' : 'blue.600' }
            />
          </Box>
        ) }

        <Box w={{ base: '100%', lg: icon ? 'calc(100% - 56px)' : '100%' }} overflow="hidden">
          <Skeleton
            isLoaded={ !isLoading }
            w="fit-content"
            borderRadius="sm"
          >
            <Text
              fontSize="xs"
              fontWeight={ 500 }
              color={ labelColor }
              lineHeight="16px"
              textTransform="uppercase"
              letterSpacing="0.05em"
              noOfLines={ 1 }
            >
              { label }
            </Text>
          </Skeleton>

          <Skeleton
            isLoaded={ !isLoading }
            display="flex"
            alignItems="baseline"
            mt={ 1 }
            borderRadius="sm"
          >
            <Text
              fontWeight={ 700 }
              fontSize="lg"
              lineHeight={ 6 }
              color={ valueColor }
              as="div"
              display="flex"
              alignItems="baseline"
              gap={ 1 }
            >
              { valuePrefix && <chakra.span whiteSpace="pre" fontSize="sm" fontWeight={ 600 }>{ valuePrefix }</chakra.span> }
              { typeof value === 'string' ? (
                <TruncatedValue isLoading={ isLoading } value={ value }/>
              ) : (
                value
              ) }
              { valuePostfix && <chakra.span whiteSpace="pre" fontSize="sm" fontWeight={ 600 }>{ valuePostfix }</chakra.span> }
              { diff && Number(diff) > 0 && (
                <>
                  <Text ml={ 1 } mr={ 0.5 } color="green.500" fontSize="sm" fontWeight={ 600 }>
                    +{ diffFormatted || Number(diff).toLocaleString() }
                  </Text>
                  <Text color="gray.600" fontSize="xs">({ diffPeriod })</Text>
                </>
              ) }
              { period && <Text color="gray.600" fontSize="xs" fontWeight={ 400 } ml={ 1 }>({ period })</Text> }
            </Text>
          </Skeleton>
        </Box>

        { typeof hint === 'string' ? (
          <Skeleton isLoaded={ !isLoading } alignSelf="flex-start" borderRadius="base" mt={ 1 }>
            <Hint label={ hint } boxSize={ 5 } color={ labelColor }/>
          </Skeleton>
        ) : hint }
      </Flex>
    </Container>
  );
};

export default chakra(StatsWidget);
